import {
  CircularProgress as MuiCircularProgress,
  CircularProgressProps as MuiCircularProgressProps,
} from '@mui/material';
import { forwardRef } from 'react';

/**
 * CircularProgress component props.
 *
 * A thin wrapper around MUI's CircularProgress that preserves full type compatibility
 * including support for theme palette extension via module augmentation.
 *
 * Common sizes: 16 (inline), 20 (button), 24 (small section), 40 (section/overlay)
 */
export type CircularProgressProps = Omit<MuiCircularProgressProps, 'color' | 'variant'> & {
  /**
   * The color of the component.
   * Supports theme palette colors including custom additions via module augmentation.
   * @default 'primary'
   */
  color?: MuiCircularProgressProps['color'];

  /**
   * The variant to use.
   * Use indeterminate for loading states.
   * Use determinate to show progress with a value.
   * @default 'indeterminate'
   */
  variant?: MuiCircularProgressProps['variant'];
};

/**
 * CircularProgress Component
 *
 * A loading spinner component that wraps Material UI's CircularProgress
 * with standardized theming and usage patterns.
 *
 * **Features:**
 * - Indeterminate mode for general loading states
 * - Determinate mode for progress tracking
 * - Multiple color variants matching theme palette
 * - Flexible sizing for different contexts
 * - Full MUI CircularProgress props support
 *
 * **Common Usage Patterns:**
 * - Button loading states (size 20, color inherit)
 * - Section overlays (size 40, color primary)
 * - Inline indicators (size 16-24)
 * - Progress tracking (determinate mode with value)
 *
 * @example
 * ```tsx
 * // Basic loading spinner
 * <CircularProgress />
 *
 * // Button loading state
 * <Button startIcon={<CircularProgress size={20} color="inherit" />}>
 *   Loading...
 * </Button>
 *
 * // Determinate progress
 * <CircularProgress variant="determinate" value={75} />
 *
 * // Custom size and color
 * <CircularProgress size={40} color="secondary" />
 * ```
 */
export const CircularProgress = forwardRef<HTMLSpanElement, CircularProgressProps>(
  ({ color = 'primary', variant = 'indeterminate', ...props }, ref) => (
    <MuiCircularProgress ref={ref} color={color} variant={variant} {...props} />
  )
);

CircularProgress.displayName = 'CircularProgress';
